import { View } from "@/components/ui/view";
import i18n from 'i18n-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, RefreshControl as RNRefreshControl } from 'react-native';
import { createNativeWrapper, FlatList } from 'react-native-gesture-handler';
import { useDispatch, useSelector } from 'react-redux';

import { useIsFocused } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import FloatingLabelInput from "@/components/basic/FloatingLabelInput";
import Footer from '@/components/basic/Footer';
import ModalConfirm from "@/components/basic/ModalConfirm";
import WorkflowListElement from '@/components/workflow/WorkflowListElement';
import SearchListElement from '@/components/workflow/SearchListElement';
import { switchScreen } from '@/hooks/navigate';
import useRefreshEffect from '@/hooks/refreshed';
import { backendRequest } from '@/stores/thunks';
import ModalFilter from '@/screens/modals/ModalFilter';
import { taskDelete } from "@/stores/mainSlice";

let lut = [];
for (let i = 0; i < 256; i++) {
    lut[i] = (i < 16 ? '0' : '') + (i).toString(16);
}

function guid7() {
    let d0 = Math.random() * 0xffffffff | 0;
    let d1 = Math.random() * 0xffffffff | 0;
    let d2 = Math.random() * 0xffffffff | 0;
    let d3 = Math.random() * 0xffffffff | 0;
    // let pfx = lut[d0 & 0xff]
    let pfx = 'FF'
    return pfx + lut[d0 >> 8 & 0xff] + lut[d0 >> 16 & 0xff] + lut[d0 >> 24 & 0xff] + '-' +
        lut[d1 & 0xff] + lut[d1 >> 8 & 0xff] + '-' + lut[d1 >> 16 & 0x0f | 0x40] + lut[d1 >> 24 & 0xff] + '-' +
        lut[d2 & 0x3f | 0x80] + lut[d2 >> 8 & 0xff] + '-' + lut[d2 >> 16 & 0xff] + lut[d2 >> 24 & 0xff] +
        lut[d3 & 0xff] + lut[d3 >> 8 & 0xff] + lut[d3 >> 16 & 0xff] + lut[d3 >> 24 & 0xff];
}

// TODO: revisit when switched to SDK46 or RNGH is updated, this is a "fix" neccessary on Android
export const RefreshControl = createNativeWrapper(RNRefreshControl, {
    disallowInterruption: true,
    shouldCancelWhenOutside: false,
});

const SearchHeader = ({ updateSearch, search }) => {
    return (
        <View className="mt-5 mb-2 items-center">
            <FloatingLabelInput label={i18n.t('searchScreen.search')} box='w-[95%]' h='h-[45px]' placeholder={i18n.t('searchScreen.search')} onChangeText={updateSearch} value={search} icon='search' />
        </View>
    );
}

export default function Workflows({ navigation }) {
    const isOffScreen = !useIsFocused() && Platform.OS !== 'web'
    const ITEM_HEIGHT = 72
    const workflows = useSelector((state) => state.workflows)
    const user = useSelector((state) => state.user)
    const taskFilter = useSelector((state) => state.settings.taskFilter)
    const rasterView = useSelector((state) => state.settings.rasterView)
    const pinned = useSelector((state) => state.settings.pinned || [])
    const changed = useSelector((state) => state.settings.changed || [])
    const server = useSelector((state) => state.server)

    const actions = [
        { icon: 'keyboard-arrow-left', text: 'footer.back', component: 'BACK' },
        { icon: 'home', text: 'footer.home', component: 'Home' },
        { icon: taskFilter == 0 ? 'filter-outline' : 'filter', lib: 'mc', text: 'workflowsScreen.filter', exec: showFilter }
    ]

    const filterTasks = (taskid) => {
        return !workflows[taskid].simulatorPreview && !workflows[taskid].deleted && (
            taskFilter == 0
            || taskFilter == 1 && workflows[taskid].owned
            || taskFilter == 2 && !workflows[taskid].owned && !workflows[taskid].public
            || taskFilter == 3 && workflows[taskid].public
            || taskFilter == 4 && workflows[taskid].studio
            || taskFilter == 5 && !workflows[taskid].studio
        )
    }

    const updateData = () => {
        return Object.keys(workflows)
            .filter(taskid => filterTasks(taskid) && (filter == '' || (workflows[taskid].title.toLowerCase().includes(filter.toLowerCase())
            || workflows[taskid].text.toLowerCase().includes(filter.toLowerCase()))))
            .map((taskid) => workflows[taskid])
            .sort((a, b) => {
                const aTitle = (a.title || '').toLowerCase()
                const bTitle = (b.title || '').toLowerCase()
                if (a.id.length == 1 && b.id.length == 1)
                    return aTitle > bTitle ? 1 : aTitle < bTitle ? -1 : 0
                if (a.id.length == 1)
                    return 1
                if (b.id.length == 1)
                    return -1
                if (pinned.includes(a.id) && pinned.includes(b.id))
                    return aTitle > bTitle ? 1 : aTitle < bTitle ? -1 : 0
                if (pinned.includes(a.id))
                    return -1
                if (pinned.includes(b.id))
                    return 1
                return aTitle > bTitle ? 1 : aTitle < bTitle ? -1 : 0
            })
    }

    const [filter, setFilter] = useState('')
    const [data, setData] = useState(() => updateData())
    const [showModal, setShowModal] = useState(false)
    const [deleteInfo, setDeleteInfo] = useState('workflowsScreen.confirmText')
    const [showModalFilter, setShowModalFilter] = useState(false)
    const [deleteWorkflowId, setDeleteWorkflowId] = useState()
    const dispatch = useDispatch()
    const [scrollPos, setScrollPos] = useState(0)

    const [refreshing, setRefreshing] = useState(false);
    const refreshRef = useRef(null);
    const scrollView = useRef(null);

    const insets = useSafeAreaInsets();

    const onRefresh = useCallback(() => {
        if (!user.demo) {
            setRefreshing(true);
            console.log('Refresh')
            dispatch(backendRequest('GET_WORKFLOWS_INLINE', '/contact/user', null, 'GET', false))
        }
    }, []);

    const onDelete = (taskid) => {
        console.log('onDelete', workflows[taskid].id, workflows[taskid].title)
        if (workflows[taskid].shared && !workflows[taskid].public) {
            setDeleteWorkflowId(taskid)
            setDeleteInfo('workflowsScreen.confirmText')
            setShowModal(true)
        } else if(workflows[taskid].public) {
            setDeleteWorkflowId(taskid)
            setDeleteInfo('workflowsScreen.confirmPublicText')
            setShowModal(true)
        } else {
            if (rasterView) {
                setDeleteWorkflowId(taskid)
                setDeleteInfo('workflowsScreen.confirmDelete')
                setShowModal(true)
            } else {
                setData(data.filter(item => item.id != taskid))
                dispatch(taskDelete({ taskid: taskid }))
            }
        }
    }

    const confirmDelete = (confirm) => {
        if (confirm) {
            setData(data.filter(item => item.id != deleteWorkflowId))
            dispatch(taskDelete({ taskid: deleteWorkflowId }))
        }
        setDeleteWorkflowId(null)
        setShowModal(false)
    }

    useEffect(() => {
        navigation.setOptions({ info: i18n.t('workflowsScreen.info' + taskFilter) })
    }, [taskFilter])

    // set the relation between redux state and local state
    // redux state triggers update of local state
    useRefreshEffect(() => {
        console.log('workflows from state')
        // setData(Object.keys(workflows).filter(taskid => !workflows[taskid].deleted).map((taskid) => workflows[taskid]))
        setData(updateData())
        setRefreshing(false)
        navigation.setOptions({ info: i18n.t('workflowsScreen.info' + taskFilter) })
    }, [workflows, taskFilter, pinned.length])

    useRefreshEffect(() => {
        if (!isOffScreen && Platform.OS !== 'web') {
            scrollView.current.scrollToOffset({ offset: scrollPos, animated: false })
        }
    }, [isOffScreen])

    function handleScroll(event) {
        setScrollPos(event.nativeEvent.contentOffset.y)
    }

    const actionSearch = () => {
        switchScreen(navigation, 'Search')
    }

    function showFilter(event) {
        setShowModalFilter(true)
    }

    const actionAdd = () => {
        switchScreen(navigation, 'WorkflowEdit', { taskid: guid7(), title: 'footer.add' })
    }

    const updateSearch = (search) => {
        console.log(search)
        setFilter(search)
        setData(Object.keys(workflows).filter(taskid => filterTasks(taskid)
            && (search == '' || (workflows[taskid].title.toLowerCase().includes(search.toLowerCase())
                || workflows[taskid].text.toLowerCase().includes(search.toLowerCase()))))
            .map((taskid) => workflows[taskid])
            .sort((a, b) => {
                const aTitle = (a.title || '').toLowerCase()
                const bTitle = (b.title || '').toLowerCase()
                if (a.id.length == 1 && b.id.length == 1)
                    return aTitle > bTitle ? 1 : aTitle < bTitle ? -1 : 0
                if (a.id.length == 1)
                    return 1
                if (b.id.length == 1)
                    return -1
                if (pinned.includes(a.id) && pinned.includes(b.id))
                    return aTitle > bTitle ? 1 : aTitle < bTitle ? -1 : 0
                if (pinned.includes(a.id))
                    return -1
                if (pinned.includes(b.id))
                    return 1
                return aTitle > bTitle ? 1 : aTitle < bTitle ? -1 : 0
            }))
    }

    const renderItem = ({ item, index }) => {
        return (
            rasterView ?
                <SearchListElement navigation={navigation} task={item} onDelete={(item.owned || item.public) && item.step == -1 && !item.locked ? onDelete : null} icon={pinned.includes(item.id) ? 'push-pin' : null} changed={changed.includes(item.id)}/>
                :
                <WorkflowListElement navigation={navigation} taskid={item.id} showProgress={true} showTimer={false} onDelete={(item.owned || item.public) && item.step == -1 && !item.locked ? onDelete : null} icon={pinned.includes(item.id) ? 'push-pin' : null} changed={changed.includes(item.id)} />
        )
    }

    const getItemLayout = (item, index) => ({ length: ITEM_HEIGHT, offset: ITEM_HEIGHT * index, index: index })

    const viewProps = rasterView ? {
        numColumns: 2,
        columnWrapperStyle: { justifyContent: 'space-around' }
    } : {}

    const refreshProps = server.online && Platform.OS !== 'web' ?
        {
            onScroll: handleScroll,
            scrollEventThrottle: 64,
            waitFor: refreshRef,
            refreshControl:
                <RefreshControl
                    ref={refreshRef}
                    refreshing={refreshing}
                    onRefresh={onRefresh}
                    enabled={Platform.OS !== 'android'}
                />
        } : {}
    return (isOffScreen ? <></> : <View className="bg-secondary-50 flex-1">
        <FlatList ref={scrollView} className="bg-secondary-50" contentContainerStyle={{ paddingBottom: 140 + insets.bottom }}
            ListHeaderComponent={<SearchHeader updateSearch={updateSearch} search={filter} />}
            {...viewProps}
            data={data}
            renderItem={renderItem}
            initialNumToRender={8}
            maxToRenderPerBatch={10}
            windowSize={10}
            getItemLayout={getItemLayout}
            keyExtractor={item => item.id}
            decelerationRate={0}
            snapToInterval={ITEM_HEIGHT}
            snapToAlignment='start'
            paddingTop={7}
            {...refreshProps}
        />
        <ModalConfirm text={deleteInfo} title={'workflowsScreen.delete'} showModal={showModal} closeModal={confirmDelete} />
        <ModalFilter showModal={showModalFilter} setShowModal={setShowModalFilter} />
        <Footer navigation={navigation} actions={actions} button={{ action: actionAdd, info: 'footer.add' }} />
    </View>);
}
